import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  input,
  model,
  output,
} from '@angular/core';
import flatpickr from 'flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

let uiDateInputUid = 0;

/** Tipos de selector soportados por el átomo. */
export type UiDateInputKind = 'date' | 'datetime' | 'time';

/**
 * Átomo: selector de fecha/fecha-hora basado en Flatpickr.
 * Misma API visual que ui-input (label, hints, estado de error).
 */
@Component({
  selector: 'ui-date-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  host: {
    class: 'ui-date-input-host',
    '[class.ui-date-input-host--block]': 'block()',
  },
})
export class UiDateInputComponent implements AfterViewInit, OnDestroy {
  @ViewChild('inputEl') private readonly inputEl!: ElementRef<HTMLInputElement>;

  readonly kind = input<UiDateInputKind>('date');
  readonly label = input('');
  readonly value = model<string>('');
  readonly placeholder = input('');

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly showLabel = input(true);
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);

  readonly name = input<string | undefined>(undefined);
  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly valueChange = output<string>();
  readonly blurred = output<FocusEvent>();
  readonly focused = output<FocusEvent>();

  protected readonly generatedId = `ui-date-input-${++uiDateInputUid}`;
  protected readonly inputId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly messageId = computed(() => `${this.inputId()}-message`);
  protected readonly ariaDescribedBy = computed(() =>
    this.message() ? this.messageId() : null,
  );
  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );
  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  private fp?: FlatpickrInstance;
  private scrollParent?: HTMLElement;
  private readonly closeOnScroll = () => this.fp?.close();

  constructor() {
    effect(() => {
      if (!this.fp) return;
      this.fp.set('clickOpens', !this.disabled() && !this.readonly());
    });

    effect(() => {
      const value = this.value();
      if (!this.fp) return;
      if (value) {
        this.fp.setDate(value, false);
      } else {
        this.fp.clear(false);
      }
    });
  }

  ngAfterViewInit(): void {
    const kind = this.kind();
    const isTime = kind === 'time';
    const isDateTime = kind === 'datetime';

    const instance = flatpickr(this.inputEl.nativeElement, {
      locale: Spanish,
      dateFormat: isTime ? 'H:i' : isDateTime ? 'd/m/Y H:i' : 'd/m/Y',
      enableTime: isTime || isDateTime,
      noCalendar: isTime,
      time_24hr: true,
      allowInput: true,
      disableMobile: true,
      clickOpens: !this.disabled() && !this.readonly(),
      onChange: (_dates, dateStr) => {
        this.value.set(dateStr);
        this.valueChange.emit(dateStr);
      },
    });

    this.fp = Array.isArray(instance) ? instance[0] : instance;

    if (this.value()) {
      this.fp?.setDate(this.value(), false);
    }

    // Cierra el calendario al hacer scroll en el contenedor padre,
    // evitando que el popup quede desacoplado del input.
    this.scrollParent = this.findScrollParent(this.inputEl.nativeElement);
    this.scrollParent.addEventListener('scroll', this.closeOnScroll, { passive: true });
  }

  ngOnDestroy(): void {
    this.fp?.destroy();
    this.scrollParent?.removeEventListener('scroll', this.closeOnScroll);
  }

  /**
   * Recorre el árbol DOM hacia arriba hasta encontrar el primer
   * ancestro con overflow: auto | scroll. Retorna documentElement
   * como fallback.
   */
  private findScrollParent(el: HTMLElement): HTMLElement {
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(node);
      if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
        return node;
      }
      node = node.parentElement;
    }
    return document.documentElement;
  }

  /**
   * Máscara automática: inserta '/' (y espacio para datetime) mientras
   * el usuario escribe, sin que tenga que tipear los separadores.
   * Formato date:     dd/mm/aaaa
   * Formato datetime: dd/mm/aaaa hh:mm
   */
  protected onDateMask(event: Event): void {
    const el = event.target as HTMLInputElement;
    const kind = this.kind();
    const cursor = el.selectionStart ?? el.value.length;

    const digits = el.value.replace(/\D/g, '');
    let masked = '';

    if (kind === 'time') {
      if (digits.length > 0) masked = digits.slice(0, 2);
      if (digits.length > 2) masked += ':' + digits.slice(2, 4);
    } else if (kind === 'datetime') {
      if (digits.length > 0) masked = digits.slice(0, 2);
      if (digits.length > 2) masked += '/' + digits.slice(2, 4);
      if (digits.length > 4) masked += '/' + digits.slice(4, 8);
      if (digits.length > 8) masked += ' ' + digits.slice(8, 10);
      if (digits.length > 10) masked += ':' + digits.slice(10, 12);
    } else {
      if (digits.length > 0) masked = digits.slice(0, 2);
      if (digits.length > 2) masked += '/' + digits.slice(2, 4);
      if (digits.length > 4) masked += '/' + digits.slice(4, 8);
    }

    if (el.value !== masked) {
      el.value = masked;
      const separatorsBefore = (masked.slice(0, cursor).match(/[/\s:]/g) ?? []).length;
      const newPos = Math.min(cursor + separatorsBefore, masked.length);
      el.setSelectionRange(newPos, newPos);
    }
  }

  protected onBlur(event: FocusEvent): void {
    this.blurred.emit(event);
  }

  protected onFocus(event: FocusEvent): void {
    this.focused.emit(event);
  }
}
